import { useParams, Link } from 'react-router-dom';
import SEOMeta from '@/components/base/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { getComicBySlug, featuredComics } from '@/mocks/comics';
import { testimonials } from '@/mocks/testimonials';

export default function ComicDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = getComicBySlug(slug || '');

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-black text-foreground-50">漫画未找到</h1>
          <Link to="/" className="mt-4 inline-block text-primary-400 hover:text-primary-300">返回首页</Link>
        </div>
      </div>
    );
  }

  const formatViews = (views: number) => {
    if (views >= 10000000) return `${(views / 10000000).toFixed(1)}千万`;
    if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
    return views.toString();
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://ynbskj.cn/' },
      { '@type': 'ListItem', position: 2, name: comic.category, item: `http://ynbskj.cn/category/${comic.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: comic.title, item: `http://ynbskj.cn/comic/${comic.slug}` },
    ],
  };

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - ${comic.category}韩国漫画`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: '2026-01-01',
    dateModified: comic.updatedAt,
    image: comic.coverImage,
    publisher: {
      '@type': 'Organization',
      name: '韩漫网',
      url: 'http://ynbskj.cn/',
    },
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${comic.title}是免费阅读的吗？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `是的，《${comic.title}》在韩漫网完全免费阅读，无需注册，无需付费。`,
        },
      },
      {
        '@type': 'Question',
        name: `${comic.title}的更新频率如何？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `《${comic.title}》目前${comic.status === '连载中' ? '保持稳定更新' : '已完结'}，共${comic.chapters}话。`,
        },
      },
    ],
  };

  const reviewLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: comic.title,
    description: comic.description,
    image: comic.coverImage,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: comic.rating,
      bestRating: '10',
      ratingCount: Math.floor(comic.views / 1000),
    },
  };

  return (
    <>
      <SEOMeta
        title={`${comic.title} - ${comic.category}韩国漫画免费在线阅读`}
        description={`${comic.title}，${comic.author}作品。${comic.description.slice(0, 120)}。韩漫网免费提供${comic.title}在线免费阅读，画质高清。`}
        keywords={`${comic.title},${comic.category}漫画,韩国漫画,${comic.author},${comic.tags.join(',')},免费漫画在线阅读`}
        canonicalUrl={`http://ynbskj.cn/comic/${comic.slug}`}
        ogType="article"
        ogImage={comic.coverImage}
        jsonLd={[breadcrumbLd, articleLd, faqLd, reviewLd]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-14">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', to: '/' },
                  { label: comic.category, to: `/category/${comic.categorySlug}` },
                  { label: comic.title },
                ]}
              />
            </div>

            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
              <div className="lg:w-[38%]">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 text-[11px] font-label font-medium text-foreground-500 border border-background-300/60 rounded-full whitespace-nowrap">
                  {comic.category}
                  <span className="text-foreground-700">·</span>
                  {comic.status}
                </span>

                <h1 className="mt-5 text-2xl md:text-4xl font-heading font-black text-foreground-50 leading-tight">
                  {comic.title}
                  <span className="block text-xl md:text-2xl text-accent-400 font-bold mt-1">
                    {comic.titleEn}
                  </span>
                </h1>

                <p className="mt-6 text-sm text-foreground-500 leading-relaxed">
                  {comic.description}
                </p>

                <div className="mt-6 flex flex-wrap items-center gap-2">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 text-[11px] rounded-full bg-background-200/40 text-foreground-400 whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="flex items-center gap-6 md:gap-10 mt-8 pt-6 border-t border-background-200/40">
                  <div>
                    <p className="text-2xl md:text-3xl font-heading font-black text-foreground-50">
                      {comic.rating}
                    </p>
                    <p className="text-[11px] text-foreground-500 mt-0.5">评分</p>
                  </div>
                  <div>
                    <p className="text-2xl md:text-3xl font-heading font-black text-foreground-50">
                      {comic.chapters}
                    </p>
                    <p className="text-[11px] text-foreground-500 mt-0.5">总章节</p>
                  </div>
                  <div>
                    <p className="text-2xl md:text-3xl font-heading font-black text-foreground-50">
                      {formatViews(comic.views)}
                    </p>
                    <p className="text-[11px] text-foreground-500 mt-0.5">人气值</p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-8">
                  <button
                    type="button"
                    className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-primary-500 text-background-950 font-semibold text-sm hover:bg-primary-400 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-play-circle-line text-lg"></i>
                    开始阅读
                  </button>
                  <button
                    type="button"
                    className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-background-200/40 text-foreground-200 text-sm hover:bg-background-200/60 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-heart-line text-base"></i>
                    收藏
                  </button>
                  <button
                    type="button"
                    className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-background-200/40 text-foreground-200 text-sm hover:bg-background-200/60 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-share-line text-base"></i>
                    分享
                  </button>
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-background-200/30">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-user-line text-foreground-400 text-sm"></i>
                    </div>
                    <span className="text-xs text-foreground-400 whitespace-nowrap">作者：{comic.author}</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-background-200/30">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-calendar-line text-foreground-400 text-sm"></i>
                    </div>
                    <span className="text-xs text-foreground-400 whitespace-nowrap">更新：{comic.updatedAt}</span>
                  </div>
                </div>
              </div>

              <div className="lg:w-[62%]">
                <div className="relative flex gap-4">
                  <div className="w-[40%] aspect-[3/4] rounded-lg overflow-hidden shadow-2xl">
                    <img
                      src={comic.coverImage}
                      alt={comic.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="w-[55%] aspect-[3/4] rounded-lg overflow-hidden shadow-2xl mt-8">
                    <img
                      src={comic.coverImage}
                      alt={`${comic.title} 封面`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-16">
              <h2 className="text-xl font-heading font-bold text-foreground-50 mb-8">章节列表</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {Array.from({ length: Math.min(12, comic.chapters) }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-200/30 hover:bg-background-200/50 transition-colors cursor-pointer text-left"
                  >
                    <span className="text-sm text-foreground-300 whitespace-nowrap">
                      第 {comic.chapters - i} 话
                    </span>
                    <span className="text-[10px] text-foreground-600 whitespace-nowrap">
                      {comic.status === '已完结' && i === 0 ? '最终话' : '免费'}
                    </span>
                  </button>
                ))}
              </div>
              {comic.chapters > 12 && (
                <div className="text-center mt-6">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 px-6 py-3 rounded-full bg-background-200/40 text-foreground-300 text-sm hover:bg-background-200/60 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    查看全部 {comic.chapters} 话
                    <i className="ri-arrow-down-s-line"></i>
                  </button>
                </div>
              )}
            </div>

            <div className="mt-16">
              <h2 className="text-xl font-heading font-bold text-foreground-50 mb-8">读者评论</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {testimonials.slice(0, 3).map((t) => (
                  <div
                    key={t.id}
                    className="bg-background-100 rounded-lg p-5 md:p-6"
                  >
                    <div className="flex items-center gap-0.5 mb-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="w-4 h-4 flex items-center justify-center">
                          <i
                            className={`text-sm ${
                              i < t.rating
                                ? 'ri-star-fill text-primary-400'
                                : 'ri-star-line text-foreground-700'
                            }`}
                          ></i>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-foreground-500 leading-relaxed">{t.comment}</p>
                    <div className="flex items-center gap-3 mt-4 pt-4 border-t border-background-200/40">
                      <div className="w-8 h-8 rounded-full overflow-hidden shrink-0">
                        <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-foreground-50">{t.name}</p>
                        <p className="text-[10px] text-foreground-600">{t.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-16">
              <h2 className="text-xl font-heading font-bold text-foreground-50 mb-6">推荐漫画</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {featuredComics.filter((c) => c.slug !== comic.slug).slice(0, 5).map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}